import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Trees, Sun, Wind } from 'lucide-react';

const Environment = () => {
 return (
 <div className="environment-page">
 <section className="page-hero">
 <div className="section-container">
 <h1 className="page-title">Environment</h1>
 <p className="page-subtitle">
 Learning spaces designed around how children actually learn.
 </p>
 </div>
 </section>

 <section className="environment-intro-section">
 <div className="section-container">
 <p className="intro-text">
 Nova Roots learning spaces are inspired by modern interpretations of Maasai circular 
 architecture and savanna-grounded design—calm, communal, and deeply rooted in place.
 </p>
 <div className="environment-images">
 <div className="environment-image-grid">
 <img src="/maasai-hut1.jpg" alt="Traditional circular Maasai-inspired architecture" />
 <img src="/maasai-hut2.jpg" alt="Circular hut integrated with nature" />
 </div>
 </div>
 </div>
 </section>

 <section className="design-principles-section">
 <div className="section-container">
 <h2 className="section-title">Design Principles</h2>
 <div className="principles-grid">
 <Card className="principle-card">
 <CardContent className="principle-content">
 <div className="principle-icon">
 <Trees size={28} />
 </div>
 <h3>Circular & Communal Forms</h3>
 <p>
 Learning spaces use circular and semi-circular layouts that encourage collaboration, 
 equality, and connection. No front, no back—just community.
 </p>
 </CardContent>
 </Card>

 <Card className="principle-card">
 <CardContent className="principle-content">
 <div className="principle-icon">
 <Sun size={28} />
 </div>
 <h3>Natural Materials & Earth Tones</h3>
 <p>
 Clay, sand, charcoal, muted ochre, timber, stone, woven fibre. Every material choice 
 reflects warmth, authenticity, and connection to the land.
 </p>
 </CardContent>
 </Card>

 <Card className="principle-card">
 <CardContent className="principle-content">
 <div className="principle-icon">
 <Wind size={28} />
 </div>
 <h3>Indoor–Outdoor Flow</h3>
 <p>
 Learning doesn't stop at walls. Spaces open to gardens, courtyards, and natural areas. 
 Children move fluidly between structured and unstructured environments.
 </p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="environment-experience-section">
 <div className="section-container">
 <h2 className="section-title">A Space That Supports Learning</h2>
 <div className="experience-content">
 <Card className="experience-card">
 <CardContent>
 <p>
 Nova Roots environments are calm, spacious, and human-scale. They're designed to reduce 
 overwhelm, support focus, and allow children to feel grounded and safe.
 </p>
 </CardContent>
 </Card>
 <Card className="experience-card">
 <CardContent>
 <p>
 There are quiet corners for individual work, communal areas for collaboration, and open 
 spaces for movement and exploration. Every space has purpose.
 </p>
 </CardContent>
 </Card>
 <Card className="experience-card">
 <CardContent>
 <p>
 This is not decorative design. It's intentional architecture that honours both African 
 context and modern learning science.
 </p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="relationship-section">
 <div className="section-container">
 <h2 className="section-title">Relationship to Land</h2>
 <div className="relationship-content">
 <p>
 Nova Roots children spend significant time outdoors—not as a break from learning, but as 
 part of it. They learn from the land, work with it, and develop a deep respect for natural 
 systems.
 </p>
 <p>
 The environment itself becomes a teacher: the soil, the seasons, the weather, the growth 
 cycles. Children develop an embodied understanding of their place in the world.
 </p>
 </div>
 </div>
 </section>

 <section className="what-not-section">
 <div className="section-container">
 <div className="what-not-box">
 <h3>This Does Not Mean</h3>
 <ul>
 <li>Literal huts or tribal decoration</li>
 <li>Safari or lodge aesthetics</li>
 <li>Costume imagery or pattern clichés</li>
 </ul>
 <p className="what-not-note">
 Nova Roots design is modern, intelligent, and rooted—not nostalgic or performative.
 </p>
 </div>
 </div>
 </section>
 </div>
 );
};

export default Environment;